import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/Table";
import { EnvelopeSimple, Plus, Trash, PaperPlaneRight, Clock } from "@phosphor-icons/react";

export default function ReportSchedule() {
  const [cfg, setCfg] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/report-schedule").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    const body = { daily_enabled: next.daily_enabled, monthly_enabled: next.monthly_enabled, recipients: next.recipients };
    const r = await api.put("/report-schedule", body);
    setCfg(r.data); toast.success("Schedule saved");
  };

  const toggle = (key) => save({ ...cfg, [key]: !cfg[key] });
  const addEmail = () => {
    if (!newEmail.includes("@")) return toast.error("Enter a valid email");
    save({ ...cfg, recipients: [...new Set([...(cfg.recipients || []), newEmail.trim()])] });
    setNewEmail("");
  };
  const removeEmail = (e) => save({ ...cfg, recipients: cfg.recipients.filter((x) => x !== e) });

  const sendNow = async (period) => {
    setSending(true);
    try {
      await api.post(`/reports/email-now?period=${period}`);
      toast.success(`${period} report emailed to recipients`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  if (!cfg) return <div className="p-5 text-muted-foreground">Loading…</div>;

  const Toggle = ({ on, testid, onClick }) => (
    <button data-testid={testid} onClick={onClick}
      className={`w-11 h-6 rounded-full transition-colors relative ${on ? "bg-primary" : "bg-secondary"}`}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );

  return (
    <div className="p-5 space-y-4 max-w-3xl">
      <PageHeader title="Scheduled Report Emails" subtitle="Automated daily & monthly fleet reports emailed to owners" />

      <div className="bg-card border border-border rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold flex items-center gap-2"><Clock size={16} /> Daily Fleet Report</div>
            <div className="text-xs text-muted-foreground">Every day at 8:00 AM IST · last sent: {cfg.last_daily ? new Date(cfg.last_daily).toLocaleString() : "never"}</div>
          </div>
          <Toggle on={cfg.daily_enabled} testid="toggle-daily" onClick={() => toggle("daily_enabled")} />
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <div>
            <div className="font-semibold flex items-center gap-2"><Clock size={16} /> Monthly Fleet Report</div>
            <div className="text-xs text-muted-foreground">1st of each month at 8:00 AM IST · last sent: {cfg.last_monthly ? new Date(cfg.last_monthly).toLocaleString() : "never"}</div>
          </div>
          <Toggle on={cfg.monthly_enabled} testid="toggle-monthly" onClick={() => toggle("monthly_enabled")} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-5">
        <div className="label-caps mb-2 flex items-center gap-1.5"><EnvelopeSimple size={13} /> Recipients</div>
        <div className="flex gap-2 mb-3">
          <input data-testid="recipient-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="owner@company.com"
            className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-sm" />
          <button data-testid="add-recipient" onClick={addEmail} className="flex items-center gap-1.5 bg-primary text-white font-semibold text-sm px-3 py-2 rounded-sm"><Plus size={15} weight="bold" /> Add</button>
        </div>
        <div className="space-y-1">
          {(cfg.recipients || []).length === 0 && <div className="text-xs text-muted-foreground">No recipients — add at least one owner email.</div>}
          {(cfg.recipients || []).map((e) => (
            <div key={e} className="flex items-center justify-between px-3 py-2 border border-border rounded-sm text-sm">
              <span className="font-mono">{e}</span>
              <button data-testid={`remove-${e}`} onClick={() => removeEmail(e)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button data-testid="send-daily-now" onClick={() => sendNow("daily")} disabled={sending}
          className="flex items-center gap-1.5 bg-secondary text-white font-semibold text-sm px-4 py-2 rounded-sm disabled:opacity-50">
          <PaperPlaneRight size={15} weight="fill" /> Send Daily Now
        </button>
        <button data-testid="send-monthly-now" onClick={() => sendNow("monthly")} disabled={sending}
          className="flex items-center gap-1.5 bg-secondary text-white font-semibold text-sm px-4 py-2 rounded-sm disabled:opacity-50">
          <PaperPlaneRight size={15} weight="fill" /> Send Monthly Now
        </button>
      </div>
      <p className="text-xs text-muted-foreground">Emails are sent from a platform-verified domain via the managed email integration. Recipients come from this server-side list only.</p>
    </div>
  );
}
